<template>
  <div class="iphone-container">
    <!-- iPhone Frame -->
    <div class="iphone-frame">
      <!-- Dynamic Notch -->
      <div class="iphone-notch">
        <div class="camera"></div>
        <div class="sensor"></div>
        <div class="speaker"></div>
      </div>
      <!-- Screen Content -->
      <div class="screen">
        <!-- Module Image Container with overlay -->
        <div class="white-margin-around image-container">
          <!-- If there is an image URL, show it as a background -->
          <div
              v-if="currentListViewImage"
              :style="{ backgroundImage: 'url(' + currentListViewImage + ')' }"
              class="background-image"
          >
            <!-- Text overlay with dynamic positioning and area -->
            <div
                class="overlay-text"
                :class="[
                  'text-align-' + getVerticalAlignmentClass(currentTextAlignVertical),
                  'text-area-' + getAreaClass(currentAreaForText)
                ]"
            >
              <p class="list-text">{{ stripHtmlTags(currentText) }}</p>
            </div>
          </div>

          <!-- Otherwise, show a fixed‐size grey placeholder with text overlay -->
          <div v-else class="background-placeholder">
            <div
                class="overlay-text"
                :class="[
                  'text-align-' + getVerticalAlignmentClass(currentTextAlignVertical),
                  'text-area-' + getAreaClass(currentAreaForText)
                ]"
            >
              <p class="list-text" v-html="currentText"></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "LoyaltyPointScanListViewMobilePreview",
  props: {
    initialListViewImage: String,
    initialText: String,
    initialAreaForText: {
      type: String,
      default: 'full'
    },
    initialTextAlignVertical: {
      type: String,
      default: 'bottom'
    }
  },
  data() {
    return {
      // Keep local data for event-based updates
      eventListViewImage: null,
      eventText: '',
      eventAreaForText: null,
      eventTextAlignVertical: null
    };
  },
  computed: {
    // Use computed properties to make the component reactive to prop changes
    currentListViewImage() {
      return this.eventListViewImage !== null ? this.eventListViewImage : this.initialListViewImage;
    },
    currentText() {
      return this.eventText !== '' ? this.eventText : (this.initialText || '');
    },
    currentAreaForText() {
      return this.eventAreaForText !== null ? this.eventAreaForText : (this.initialAreaForText || 'full');
    },
    currentTextAlignVertical() {
      return this.eventTextAlignVertical !== null ? this.eventTextAlignVertical : (this.initialTextAlignVertical || 'bottom');
    }
  },
  methods: {
    stripHtmlTags(html) {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent || div.innerText || '';
    },
    getAreaClass(value) {
      if (value == 1 || value === 'full') return 'full';
      if (value == 2 || value === 'half') return 'half';
      return 'full'; // default
    },
    getVerticalAlignmentClass(value) {
      if (value == 1 || value === 'top') return 'top';
      if (value == 2 || value === 'middle') return 'middle';
      if (value == 3 || value === 'bottom') return 'bottom';
      return 'bottom';
    }
  },
  mounted() {
    window.addEventListener("list-view-image-selected", (event) => {
      this.eventListViewImage = event.detail;
    });
    window.addEventListener("list-text-updated", (event) => {
      this.eventText = event.detail;
    });
    window.addEventListener("area-for-text-updated", (event) => {
      this.eventAreaForText = event.detail;
    });
    window.addEventListener("text-align-vertical-updated", (event) => {
      this.eventTextAlignVertical = event.detail;
    });
  }
};
</script>

<style scoped>
/* Container for centering the iPhone preview */
.iphone-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* iPhone Frame */
.iphone-frame {
  width: 390px; /* iPhone width */
  height: 844px; /* iPhone height */
  background: #fff;
  border-radius: 40px;
  border: 8px solid #222;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

/* Notch (Dynamic Island style) */
.iphone-notch {
  width: 140px;
  height: 35px;
  background: #222;
  border-radius: 20px;
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 5px;
}

/* Notch elements */
.camera {
  width: 12px;
  height: 12px;
  background: black;
  border-radius: 50%;
}

.sensor {
  width: 8px;
  height: 8px;
  background: grey;
  border-radius: 50%;
}

.speaker {
  width: 40px;
  height: 5px;
  background: darkgray;
  border-radius: 3px;
}

/* Screen area */
.screen {
  flex-grow: 1;
  background: white;
  padding: 0;
  margin-top: 50px; /* Space for notch */
  position: relative;
}

/* Image container to allow overlay positioning */
.image-container {
  position: relative;
}

/* Background Image (listViewImage) */
.background-image {
  width: 100%;
  height: 200px;
  background-size: cover;
  background-position: center;
  border-radius: 5px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  position: relative;
}

/* Grey placeholder if no image is selected */
.background-placeholder {
  width: 100%;
  height: 200px;
  background-color: #efefef;
  border-radius: 5px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  position: relative;
}

/* Base overlay text container */
.overlay-text {
  position: absolute;
  color: black;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
  padding: 15px;
  box-sizing: border-box; /* Include padding in width calculation */
}

/* Text area settings */
.text-area-full {
  left: 0;
  right: 0;
}

.text-area-half {
  left: 50%;
  right: 0;
}

/* Vertical alignment settings */
.text-align-top {
  top: 0;
  display: flex;
  align-items: flex-start;
}

.text-align-middle {
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
}

.text-align-bottom {
  bottom: 0;
  display: flex;
  align-items: flex-end;
}

/* List text styling */
.list-text {
  font-size: 16px;
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.4;
  max-width: 100%;
}

.white-margin-around {
  margin: 10px;
}
</style>